"use client";

import { useState, useEffect } from "react";
import dayjs from "dayjs";
import axios from "axios";

import { useTina, tinaField, coloredText, getIcon, cn } from "@/lib/utils";
import Section from "@/components/Section";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import Pill from "@/components/Pill";
import { CheckCheck, Loader2, CalendarIcon, ClockIcon } from "lucide-react";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import advancedFormat from "dayjs/plugin/advancedFormat";

import dynamic from "next/dynamic";

const Lottie = dynamic(() => import("react-lottie-player"), { ssr: false });

import animationData from "@/assets/success.json";

dayjs.extend(advancedFormat);

export default function RequestDemoPage(props) {
  const { data } = useTina({
    query: props.query,
    variables: props.variables,
    data: props.data,
  });

  const { header, demoDetails, successMessage } = data.requestDemo;

  const [availabilitySlots, setAvailabilitySlots] = useState(null);
  const [timezone, setTimezone] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
  }, []);

  useEffect(() => {
    if (!timezone) return;
    const fetchAvailability = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`/api/availability`, {
          params: {
            timezone: timezone,
          },
        });
        setAvailabilitySlots(response.data.data);
      } catch (error) {
        console.error("Error fetching availability:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAvailability();
  }, [timezone]);

  return (
    <div>
      <Section
        wrapperClassName="bg-background-blackish"
        className="grid grid-cols-1 lg:grid-cols-[1fr_1.25fr] gap-12 lg:gap-20"
      >
        <Header header={header} />
        <DemoForm
          availabilitySlots={availabilitySlots}
          successMessage={successMessage}
          isLoading={isLoading}
        />
      </Section>
      <DemoDetails demoDetails={demoDetails} />
    </div>
  );
}

const Header = ({ header }) => {
  const title = coloredText(header?.title);
  return (
    <div className="flex flex-col gap-4 justify-center">
      <h1
        data-tina-field={tinaField(header, "title")}
        dangerouslySetInnerHTML={{ __html: title }}
      />
      <p
        data-tina-field={tinaField(header, "description")}
        className="p-xl text-foreground-300 max-w-[800px]"
      >
        {header?.description}
      </p>

      <div className="flex flex-col gap-4 mt-4">
        {header?.demoBenefits?.map((benefit, index) => (
          <div key={index} className="flex items-center gap-4">
            <CheckCheck className="text-primary-300 size-6" />
            <p
              className="p-lg"
              data-tina-field={tinaField(
                header,
                `demoBenefits.${index}.description`
              )}
            >
              {benefit.description}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

const formSchema = z.object({
  fullName: z.string().min(1, "Required"),
  email: z.string().email({
    message: "Invalid email",
  }),
  date: z.date({
    required_error: "Select a date",
    message: "Select a date",
  }),
  time: z.string().min(1, "Select a time slot"),
  message: z.string().optional(),
});

function DemoForm({ availabilitySlots, successMessage, isLoading }) {
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: "",
      email: "",
      date: "",
      time: "",
      message: "",
    },
  });

  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  const [timePopoverOpen, setTimePopoverOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [timezone, setTimezone] = useState(null);

  useEffect(() => {
    setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
  }, []);

  const availableDates = availabilitySlots
    ? Object.keys(availabilitySlots).map((date) => dayjs(date))
    : [];

  const selectedDate = form.watch("date");
  const availableTimeSlots = selectedDate
    ? availabilitySlots[dayjs(selectedDate).format("YYYY-MM-DD")] || []
    : [];

  async function onSubmit(values) {
    if (!timezone) return;
    try {
      form.clearErrors();
      await axios.post(`/api/availability`, {
        start: values.time,
        message: values.message,
        attendee: {
          name: values.fullName,
          email: values.email,
          timezone: timezone,
        },
      });
      // Reset form on success
      form.reset();
      setIsSuccess(true);
    } catch (error) {
      const errorMessage =
        error.response?.data?.details?.error?.details?.message ||
        error.response?.data?.details?.message ||
        "Something went wrong. Please try again.";
      form.setError("root", {
        type: "manual",
        message: errorMessage,
      });
    }
  }

  return (
    <div className="card !p-6 lg:!p-10 bg-background-purplish relative">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className=" grid grid-cols-1 md:grid-cols-2 gap-6 text-foreground-100 [&_label]:mb-1"
        >
          <FormField
            control={form.control}
            name="fullName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="flex-1">
                    Full Name
                    <span className="text-red-500 ml-1">*</span>
                  </span>
                  <FormMessage />
                </FormLabel>
                <FormControl>
                  <Input placeholder="John Doe" {...field} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="flex-1">
                    Email
                    <span className="text-red-500 ml-1">*</span>
                  </span>
                  <FormMessage />
                </FormLabel>
                <FormControl>
                  <Input placeholder="john@doe.com" {...field} />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="date"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>
                  <span className="flex-1">
                    Date
                    <span className="text-red-500 ml-1">*</span>
                  </span>
                  <FormMessage />
                </FormLabel>
                <Popover
                  open={datePopoverOpen}
                  onOpenChange={setDatePopoverOpen}
                >
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        disabled={isLoading}
                        className={cn(
                          "w-full border-input text-muted-foreground font-normal text-base placeholder:text-muted-foreground placeholder:text-base h-12 shadow-none hover:border-primary-200/90 hover:bg-transparent hover:text-muted-foreground",
                          field.value && "text-white hover:text-white",
                          "focus:border-primary-200/90",
                          datePopoverOpen &&
                            "border-primary-200/90 hover:border-primary-200/90"
                        )}
                      >
                        {field.value ? (
                          dayjs(field.value).format("ddd, Do MMMM")
                        ) : isLoading ? (
                          <span>Fetching availability...</span>
                        ) : (
                          <span>Pick a date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      // className="bg-background-blackish"
                      mode="single"
                      selected={field.value}
                      onSelect={(date) => {
                        field.onChange(date);
                        form.setValue("time", "");
                        setDatePopoverOpen(false);
                      }}
                      disabled={(date) => {
                        const dateString = dayjs(date).format("YYYY-MM-DD");
                        return !availableDates.some(
                          (availableDate) =>
                            availableDate.format("YYYY-MM-DD") === dateString
                        );
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="time"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>
                  <span className="flex-1">
                    Time
                    <span className="text-red-500 ml-1">*</span>
                  </span>
                  <FormMessage />
                </FormLabel>
                <Popover
                  open={timePopoverOpen}
                  onOpenChange={setTimePopoverOpen}
                  className="w-full"
                >
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full text-base border-input text-muted-foreground font-normal placeholder:text-muted-foreground placeholder:text-base h-12 shadow-none hover:border-primary-200/90 hover:bg-transparent hover:text-muted-foreground",
                          field.value && "text-white hover:text-white",
                          "focus:border-primary-200/90",
                          timePopoverOpen &&
                            "border-primary-200/90 hover:border-primary-200/90"
                        )}
                        disabled={!selectedDate}
                      >
                        {field.value ? (
                          dayjs(field.value).format("h:mm A")
                        ) : (
                          <span>Pick a time</span>
                        )}
                        <ClockIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-full p-0 bg-background-purplish"
                    align="start"
                  >
                    <div className="p-3">
                      <div className="text-sm font-medium mb-3">
                        Available Times:
                      </div>
                      <div className="grid grid-cols-3 gap-2.5">
                        {availableTimeSlots.map((slot, index) => (
                          <Button
                            key={index}
                            variant="outline"
                            className={cn(
                              "justify-center font-normal text-[13px] px-2",
                              field.value === slot.start &&
                                "bg-primary-400 text-white hover:bg-primary-400 hover:text-white"
                            )}
                            onClick={() => {
                              field.onChange(slot.start);
                              setTimePopoverOpen(false);
                            }}
                          >
                            {dayjs(slot.start).format("h:mma")}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </FormItem>
            )}
          />

          <div className="md:col-span-2">
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Message (optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      className="min-h-24 p-4"
                      placeholder="Tell us about your business and what you're looking for, or any other information you think is relevant..."
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="md:col-span-2 w-full">
            <Button
              type="submit"
              className="w-full disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={form.formState.isSubmitting}
            >
              {form.formState.isSubmitting ? (
                <Loader2 className="animate-spin size-5" />
              ) : (
                "Submit"
              )}
            </Button>

            {form.formState.errors.root && (
              <div className="text-red-500 text-sm font-medium text-center capitalize -mt-2">
                {form.formState.errors.root.message}
              </div>
            )}
          </div>
          {isSuccess && <SuccessMessage successMessage={successMessage} />}
        </form>
      </Form>
    </div>
  );
}

const DemoDetails = ({ demoDetails }) => {
  return (
    <Section
      wrapperClassName="bg-gradient-to-b from-background-purplish to-background-blackish"
      className="flex flex-col gap-12"
    >
      <div className="flex flex-col gap-5 items-center">
        <Pill data-tina-field={tinaField(demoDetails, "sectionTitle")}>
          {demoDetails?.sectionTitle}
        </Pill>
        <h2 data-tina-field={tinaField(demoDetails, "title")}>
          {demoDetails?.title}
        </h2>
        <p
          className="p-xl text-foreground-300 text-center max-w-[800px] "
          data-tina-field={tinaField(demoDetails, "subtitle")}
        >
          {demoDetails?.subtitle}
        </p>
      </div>
      <div className="gap-4 grid grid-cols-1 lg:grid-cols-3">
        {demoDetails?.items?.map((item, index) => {
          const Icon = getIcon(item?.icon);

          return (
            <div
              key={index}
              className="flex flex-col gap-8 card bg-background-blackish"
            >
              {Icon && (
                <div
                  data-tina-field={tinaField(
                    demoDetails,
                    `items.${index}.icon`
                  )}
                >
                  <Icon className="text-primary-300 size-10 stroke-[1.5px]" />
                </div>
              )}
              <div className="flex flex-col gap-3">
                <h3
                  className="h5"
                  data-tina-field={tinaField(
                    demoDetails,
                    `items.${index}.title`
                  )}
                >
                  {item?.title}
                </h3>
                <p
                  className="text-foreground-500"
                  data-tina-field={tinaField(
                    demoDetails,
                    `items.${index}.description`
                  )}
                >
                  {item?.description}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
};

const SuccessMessage = ({ successMessage }) => {
  return (
    <div className="absolute top-0 left-0 w-full h-full flex flex-col items-center justify-center bg-background-purplish rounded-lg text-center">
      <div className="w-48 min-h-48">
        <Lottie
          loop={false}
          play
          animationData={animationData}
          className="success-animation w-48 h-48"
        />
      </div>
      <div className="mt-4 flex flex-col gap-2">
        <div
          className="h4 mb-3"
          data-tina-field={tinaField(successMessage, "title")}
        >
          {successMessage?.title || "Your demo has been booked successfully!"}
        </div>
        <div
          className="p-lg max-w-[500px] text-foreground-500"
          data-tina-field={tinaField(successMessage, "subtitle")}
        >
          {successMessage?.subtitle ||
            "You should receive a confirmation email shortly."}
        </div>
      </div>
    </div>
  );
};
